import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { devisAPI } from '@/lib/api';
import api from '@/lib/api';
import { formatPrice, formatDate, getStatusColor, getStatusLabel, cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Eye,
  FileText,
  Filter,
  LayoutGrid,
  List,
  Clock,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  MoreHorizontal,
  GripVertical,
  Archive,
  Copy,
  Building2,
  ArrowUpDown,
  SlidersHorizontal,
  ArrowUp,
  ArrowDown,
  Download,
  Layers,
  ChevronDown,
  ChevronRight,
  Banknote,
  UserPlus,
  Mail,
  X,
  FileUp,
  ExternalLink,
  UserCheck,
  Loader2,
  Trash2,
  Wrench,
} from 'lucide-react';

const STATUSES = [
  { id: 'brouillon', label: 'Brouillon', icon: Clock, color: 'bg-slate-100 text-slate-700' },
  { id: 'envoye', label: 'Envoyé', icon: Send, color: 'bg-blue-100 text-blue-700' },
  { id: 'relance_1', label: 'Relancé 1', icon: Send, color: 'bg-blue-100 text-blue-700' },
  { id: 'relance_2', label: 'Relancé 2', icon: Send, color: 'bg-blue-100 text-blue-700' },
  { id: 'relance_3', label: 'Relancé 3', icon: Send, color: 'bg-amber-100 text-amber-700' },
  { id: 'accepte', label: 'Accepté', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  { id: 'refuse', label: 'Refusé', icon: XCircle, color: 'bg-red-100 text-red-700' },
  { id: 'sans_suite', label: 'Sans suite', icon: AlertCircle, color: 'bg-orange-100 text-orange-700' },
  { id: 'expire', label: 'Expiré', icon: AlertCircle, color: 'bg-orange-100 text-orange-700' },
  { id: 'archive', label: 'Archivé', icon: Archive, color: 'bg-purple-100 text-purple-700' },
];

const DevisListPage = () => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [showRelanceOnly, setShowRelanceOnly] = useState(searchParams.get('relance') === 'true');
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(searchParams.get('unassigned') === 'true');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const [showArchived, setShowArchived] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Grouping
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'status', 'client', 'vendeur', 'month'
  const [collapsedGroups, setCollapsedGroups] = useState({});
  
  // Selection state for bulk relance actions
  const [selectedDevis, setSelectedDevis] = useState([]);
  const [bulkRelanceLoading, setBulkRelanceLoading] = useState(false);
  const [clotureDialogOpen, setClotureDialogOpen] = useState(false);
  const [clotureCandidates, setClotureCandidates] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [vendeurs, setVendeurs] = useState([]);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [bulkArchiveLoading, setBulkArchiveLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    reference: true,
    client: true,
    machine: true,
    total: true,
    status: true,
    financement: true,
    date: true,
    vendeur: true,
    banque: true,
    actions: true,
  });

  // Kanban column visibility - default visible columns
  const [visibleKanbanColumns, setVisibleKanbanColumns] = useState({
    brouillon: true,
    envoye: true,
    relance_1: false,
    relance_2: false,
    relance_3: false,
    accepte: true,
    refuse: true,
    sans_suite: false,
    expire: false,
    archive: false,
  });

  // Drag & Drop state
  const [draggedDevis, setDraggedDevis] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  useEffect(() => {
    fetchDevis();
    if (isAdmin()) fetchVendeurs();
  }, []);

  const fetchDevis = async () => {
    try {
      const response = await devisAPI.getAll();
      setDevis(response.data);
    } catch (error) {
      console.error('Error fetching devis:', error);
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendeurs = async () => {
    try {
      const res = await api.get('/users');
      setVendeurs((res.data || []).filter(u => u.role === 'vendeur' || u.role === 'admin'));
    } catch (e) { console.error(e); }
  };

  const handleBulkAssign = async (vendeurId) => {
    if (selectedDevis.length === 0 || !vendeurId) return;
    setBulkAssignLoading(true);
    try {
      const res = await api.post('/devis/bulk-assign', { devis_ids: selectedDevis, vendeur_id: vendeurId });
      toast.success(`${res.data.updated} devis assignés à ${res.data.vendeur_name}`);
      setSelectedDevis([]);
      setShowAssignDialog(false);
      fetchDevis();
    } catch (err) {
      toast.error('Erreur lors de l\'assignation');
    } finally {
      setBulkAssignLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedDevis.length === 0) return;
    setBulkArchiveLoading(true);
    try {
      const res = await api.post('/devis/bulk-action', { devis_ids: selectedDevis, action: 'archive' });
      toast.success(`${res.data.updated} devis archivés`);
      setSelectedDevis([]);
      fetchDevis();
    } catch (err) {
      toast.error('Erreur lors de l\'archivage');
    } finally {
      setBulkArchiveLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDevis.length === 0) return;
    const brouillons = devis.filter(d => selectedDevis.includes(d.id) && d.status === 'brouillon');
    if (brouillons.length === 0) { toast.error('Aucun brouillon sélectionné'); return; }
    if (!window.confirm(`Supprimer définitivement ${brouillons.length} brouillon(s) ? Cette action est irréversible.`)) return;
    setBulkDeleteLoading(true);
    try {
      const res = await api.post('/devis/bulk-action', { devis_ids: selectedDevis, action: 'delete' });
      toast.success(`${res.data.deleted} brouillon(s) supprimé(s)`);
      setSelectedDevis([]);
      fetchDevis();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleStatusChange = async (devisId, newStatus) => {
    try {
      await devisAPI.updateStatus(devisId, newStatus);
      toast.success('Statut mis à jour');
      fetchDevis();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDuplicate = async (devisId) => {
    try {
      const response = await devisAPI.duplicate(devisId);
      toast.success(`Devis dupliqué: ${response.data.reference}`);
      fetchDevis();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleArchive = async (devisId) => {
    try {
      await devisAPI.archive(devisId);
      toast.success('Devis archivé');
      fetchDevis();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  // Selection handlers for bulk actions
  const handleSelectDevis = (devisId, checked) => {
    if (checked) {
      setSelectedDevis(prev => [...prev, devisId]);
    } else {
      setSelectedDevis(prev => prev.filter(id => id !== devisId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedDevis(sortedDevis.map(d => d.id));
    } else {
      setSelectedDevis([]);
    }
  };

  const isAllSelected = () => {
    return sortedDevis.length > 0 && sortedDevis.every(d => selectedDevis.includes(d.id));
  };

  const handleBulkRelance = async () => {
    if (selectedDevis.length === 0) {
      toast.error('Sélectionnez au moins un devis à relancer');
      return;
    }

    setBulkRelanceLoading(true);
    try {
      const response = await devisAPI.bulkRelance(selectedDevis);
      const data = response.data;
      
      // Check for cloture candidates
      if (data.cloture_candidates && data.cloture_candidates.length > 0) {
        setClotureCandidates(data.cloture_candidates);
        setClotureDialogOpen(true);
      }
      
      if (data.success_count > 0) {
        toast.success(`${data.success_count} relance(s) envoyée(s)`);
      }
      if (data.error_count > 0) {
        toast.error(`${data.error_count} erreur(s) lors de l'envoi`);
      }
      
      // Clear selection and refresh
      setSelectedDevis([]);
      fetchDevis();
    } catch (error) {
      console.error('Bulk relance error:', error);
      toast.error('Erreur lors de l\'envoi des relances');
    } finally {
      setBulkRelanceLoading(false);
    }
  };

  const handleBulkCloture = async () => {
    if (clotureCandidates.length === 0) return;
    
    try {
      const devisIds = clotureCandidates.map(c => c.devis_id);
      await devisAPI.bulkCloture(devisIds);
      toast.success(`${devisIds.length} devis clôturé(s)`);
      setClotureDialogOpen(false);
      setClotureCandidates([]);
      fetchDevis();
    } catch (error) {
      toast.error('Erreur lors de la clôture');
    }
  };

  const clearSelection = () => {
    setSelectedDevis([]);
  };

  // Drag & Drop handlers
  const handleDragStart = (e, devisItem) => {
    setDraggedDevis(devisItem);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', devisItem.id);
    // Add visual feedback
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedDevis(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, statusId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverColumn !== statusId) {
      setDragOverColumn(statusId);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if leaving the column area entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (draggedDevis && draggedDevis.status !== newStatus) {
      await handleStatusChange(draggedDevis.id, newStatus);
    }
    setDraggedDevis(null);
  };

  // Toggle kanban column visibility
  const toggleKanbanColumn = (columnId) => {
    setVisibleKanbanColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  // Get visible kanban statuses
  const visibleKanbanStatuses = useMemo(() => {
    return STATUSES.filter(s => visibleKanbanColumns[s.id]);
  }, [visibleKanbanColumns]);

  const filteredDevis = devis.filter((d) => {
    const matchesSearch =
      (d.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.mini_pelle_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.cabinet_snc_name && d.cabinet_snc_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    
    // Filtre "à relancer" - statuts: envoye, relance_1, relance_2, relance_3
    const relanceStatuses = ['envoye', 'relance_1', 'relance_2', 'relance_3'];
    const matchesRelance = !showRelanceOnly || relanceStatuses.includes(d.status);
    
    // Filtre "non assignés" - devis sans vendeur assigné
    const matchesUnassigned = !showUnassignedOnly || !d.assigned_to || d.assigned_to === '';
    
    // Hide archived by default unless filter is set to archive or showArchived is true
    const matchesArchive = showArchived || d.status !== 'archive' || statusFilter === 'archive';
    
    return matchesSearch && matchesStatus && matchesArchive && matchesRelance && matchesUnassigned;
  });

  // Sort function
  const sortedDevis = useMemo(() => {
    const sorted = [...filteredDevis];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      
      // Special handling for dates
      if (sortConfig.key === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Special handling for numbers
      if (sortConfig.key === 'total') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredDevis, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  // Grouping logic
  const groupedDevis = useMemo(() => {
    if (groupBy === 'none') return null;
    
    const groups = {};
    sortedDevis.forEach(d => {
      let groupKey;
      let groupLabel;
      
      switch (groupBy) {
        case 'status':
          groupKey = d.status;
          groupLabel = getStatusLabel(d.status);
          break;
        case 'client':
          groupKey = d.client_id || 'no-client';
          groupLabel = d.client_name || 'Sans client';
          break;
        case 'vendeur':
          groupKey = d.assigned_to || 'no-vendeur';
          groupLabel = d.assigned_to_name || 'Non assigné';
          break;
        case 'month':
          const date = new Date(d.created_at);
          groupKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          groupLabel = date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
          break;
        default:
          groupKey = 'other';
          groupLabel = 'Autres';
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = { label: groupLabel, items: [], total: 0 };
      }
      groups[groupKey].items.push(d);
      groups[groupKey].total += d.total || 0;
    });
    
    return groups;
  }, [sortedDevis, groupBy]);

  const toggleGroup = (groupKey) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Tout replier / Tout déplier
  const collapseAllGroups = () => {
    if (!groupedDevis) return;
    const allCollapsed = {};
    Object.keys(groupedDevis).forEach(key => {
      allCollapsed[key] = true;
    });
    setCollapsedGroups(allCollapsed);
  };

  const expandAllGroups = () => {
    setCollapsedGroups({});
  };

  // Vérifier si tous les groupes sont repliés
  const areAllGroupsCollapsed = () => {
    if (!groupedDevis) return false;
    const groupKeys = Object.keys(groupedDevis);
    return groupKeys.length > 0 && groupKeys.every(key => collapsedGroups[key]);
  };

  const getDevisByStatus = (status) => {
    return sortedDevis.filter((d) => d.status === status);
  };

  const handleExportDevis = async () => {
    try {
      const response = await devisAPI.export();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'devis_export.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // For Kanban, use visible columns, and filter out archived unless explicitly showing them
  const kanbanStatuses = useMemo(() => {
    let statuses = STATUSES.filter(s => visibleKanbanColumns[s.id]);
    if (!showArchived) {
      statuses = statuses.filter(s => s.id !== 'archive');
    }
    return statuses;
  }, [visibleKanbanColumns, showArchived]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="devis-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Devis
          </h1>
          <p className="text-slate-500 mt-1">{devis.length} devis au total</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin() && (
            <Button variant="outline" size="sm" onClick={handleExportDevis} data-testid="export-devis-btn">
              <Download className="w-4 h-4 mr-2" />
              Exporter CSV
            </Button>
          )}
          <div className="flex items-center bg-slate-100 rounded-md p-1">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-white shadow-sm' : ''}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              className={viewMode === 'kanban' ? 'bg-white shadow-sm' : ''}
              data-testid="view-kanban-btn"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          <Link to="/admin/devis/import">
            <Button variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50" data-testid="import-devis-btn">
              <FileUp className="w-4 h-4 mr-2" />
              Importer PDF
            </Button>
          </Link>
          <Link to="/admin/devis/nouveau">
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-slate-900" data-testid="create-devis-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau devis
            </Button>
          </Link>
          <Link to="/admin/devis/nouveau?mode=service">
            <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="create-service-devis-btn">
              <Wrench className="w-4 h-4 mr-2" />
              Devis prestation
            </Button>
          </Link>
        </div>
      </div>

      {/* Barre d'actions — visible dès qu'un devis est sélectionné */}
      {selectedDevis.length > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="sticky top-2 z-30 flex items-center gap-3 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-xl"
        >
          <button
            onClick={clearSelection}
            className="text-slate-400 hover:text-white transition-colors flex-shrink-0"
            data-testid="clear-selection-btn"
          >
            <X className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium">
            {selectedDevis.length} devis sélectionné{selectedDevis.length > 1 ? 's' : ''}
          </span>
          <div className="flex-1" />

          {/* Bouton unique "Actions" avec menu déroulant */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="bg-white text-slate-900 hover:bg-slate-100 font-medium gap-2"
                data-testid="bulk-actions-dropdown-btn"
              >
                Actions
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {/* Envoyer par email */}
              <DropdownMenuItem
                onClick={handleBulkRelance}
                disabled={bulkRelanceLoading}
                data-testid="action-email-btn"
                className="gap-2"
              >
                {bulkRelanceLoading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Mail className="w-4 h-4" />}
                Envoyer par email
              </DropdownMenuItem>

              {/* Réassigner à un vendeur (admin) */}
              {isAdmin() && (
                <DropdownMenuItem
                  onClick={() => setShowAssignDialog(true)}
                  data-testid="action-assign-btn"
                  className="gap-2"
                >
                  <UserCheck className="w-4 h-4" />
                  Réassigner à un vendeur
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              {/* Archiver */}
              <DropdownMenuItem
                onClick={handleBulkArchive}
                disabled={bulkArchiveLoading}
                data-testid="action-archive-btn"
                className="gap-2 text-amber-700 focus:text-amber-700"
              >
                {bulkArchiveLoading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Archive className="w-4 h-4" />}
                Archiver
              </DropdownMenuItem>

              {/* Supprimer — admin only, brouillons only */}
              {isAdmin() && selectedDevis.some(id => {
                const d = devis.find(x => x.id === id);
                return d?.status === 'brouillon';
              }) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleBulkDelete}
                    disabled={bulkDeleteLoading}
                    data-testid="action-delete-btn"
                    className="gap-2 text-red-600 focus:text-red-600"
                  >
                    {bulkDeleteLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4" />}
                    Supprimer (brouillons)
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Dialog Assigner vendeur */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Réassigner {selectedDevis.length} devis</DialogTitle>
            <DialogDescription>Choisissez le commercial qui prendra en charge ces devis.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {vendeurs.map(v => (
              <button
                key={v.id}
                onClick={() => handleBulkAssign(v.id)}
                disabled={bulkAssignLoading}
                data-testid={`assign-vendeur-${v.id}`}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600 flex-shrink-0">
                  {(v.name || v.email)[0].toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-slate-800">{v.name || v.email}</div>
                  <div className="text-xs text-slate-400 capitalize">{v.role}</div>
                </div>
                {bulkAssignLoading && <Loader2 className="w-4 h-4 animate-spin ml-auto text-slate-400" />}
              </button>
            ))}
            {vendeurs.length === 0 && <p className="text-sm text-slate-500 text-center py-4">Aucun vendeur trouvé</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cloture Dialog */}
      <Dialog open={clotureDialogOpen} onOpenChange={setClotureDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-amber-600">Devis en fin de parcours</DialogTitle>
            <DialogDescription>
              Ces devis ont déjà été relancés 3 fois. Souhaitez-vous les clôturer (passer en "Sans suite") ?
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-60 overflow-y-auto">
            <ul className="space-y-2">
              {clotureCandidates.map(c => (
                <li key={c.devis_id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="font-medium">{c.client_name}</span>
                  <span className="font-mono text-sm text-slate-500">{c.reference}</span>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setClotureDialogOpen(false)}>
              Non, garder ouverts
            </Button>
            <Button 
              onClick={handleBulkCloture}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              Oui, clôturer ({clotureCandidates.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par référence, client, machine..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-devis-input"
              />
            </div>
            {viewMode === 'list' && (
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <Select value={statusFilter} onValueChange={(value) => {
                  setStatusFilter(value);
                  setSelectedDevis([]); // Clear selection when filter changes
                }}>
                  <SelectTrigger className="w-40" data-testid="status-filter-select">
                    <SelectValue placeholder="Tous les statuts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="nouveau">🆕 Nouveau</SelectItem>
                    <SelectItem value="brouillon">Brouillon</SelectItem>
                    <SelectItem value="envoye">Envoyé</SelectItem>
                    <SelectItem value="relance_1">Relancé 1</SelectItem>
                    <SelectItem value="relance_2">Relancé 2</SelectItem>
                    <SelectItem value="relance_3">Relancé 3</SelectItem>
                    <SelectItem value="en_etude_financement">📋 En étude financement</SelectItem>
                    <SelectItem value="accepte">Accepté</SelectItem>
                    <SelectItem value="refuse">Refusé</SelectItem>
                    <SelectItem value="sans_suite">Sans suite</SelectItem>
                    <SelectItem value="expire">Expiré</SelectItem>
                    <SelectItem value="archive">Archivé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                showArchived 
                  ? "bg-purple-100 text-purple-700" 
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              )}
              data-testid="toggle-archived-btn"
            >
              <Archive className="w-4 h-4" />
              {showArchived ? 'Masquer archivés' : 'Voir archivés'}
            </button>
            
            {/* Column visibility dropdown - for list view */}
            {viewMode === 'list' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Colonnes
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuCheckboxItem
                  checked={visibleColumns.reference}
                  onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, reference: checked})}
                >
                  Référence
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={visibleColumns.client}
                  onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, client: checked})}
                >
                  Client
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={visibleColumns.machine}
                  onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, machine: checked})}
                >
                  Machine
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={visibleColumns.total}
                  onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, total: checked})}
                >
                  Total
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={visibleColumns.status}
                  onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, status: checked})}
                >
                  Statut
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={visibleColumns.date}
                  onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, date: checked})}
                >
                  Date
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={visibleColumns.vendeur}
                  onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, vendeur: checked})}
                >
                  Vendeur
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={visibleColumns.banque}
                  onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, banque: checked})}
                >
                  Banque
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* Kanban columns dropdown - for kanban view */}
            {viewMode === 'kanban' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="kanban-columns-btn">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Colonnes Kanban
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {STATUSES.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status.id}
                    checked={visibleKanbanColumns[status.id]}
                    onCheckedChange={() => toggleKanbanColumn(status.id)}
                    data-testid={`kanban-toggle-${status.id}`}
                  >
                    <status.icon className="w-3 h-3 mr-2" />
                    {status.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* Grouping dropdown */}
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-[160px]">
                <Layers className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Grouper par..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Pas de groupe</SelectItem>
                <SelectItem value="status">Par statut</SelectItem>
                <SelectItem value="client">Par client</SelectItem>
                <SelectItem value="vendeur">Par commercial</SelectItem>
                <SelectItem value="month">Par mois</SelectItem>
              </SelectContent>
            </Select>

            {/* Boutons Tout replier / Tout déplier */}
            {groupBy !== 'none' && groupedDevis && Object.keys(groupedDevis).length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={areAllGroupsCollapsed() ? expandAllGroups : collapseAllGroups}
              >
                {areAllGroupsCollapsed() ? (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Tout déplier
                  </>
                ) : (
                  <>
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Tout replier
                  </>
                )}
              </Button>
            )}

            {/* Filtre À relancer */}
            <button
              onClick={() => {
                setShowRelanceOnly(!showRelanceOnly);
                if (!showRelanceOnly) {
                  setStatusFilter('all');
                  setShowUnassignedOnly(false);
                }
              }}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                showRelanceOnly 
                  ? "bg-orange-100 text-orange-700 font-medium" 
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              )}
              data-testid="toggle-relance-btn"
            >
              <AlertCircle className="w-4 h-4" />
              À relancer
            </button>

            {/* Filtre Non assignés (admin only) */}
            {user?.role === 'admin' && (
              <button
                onClick={() => {
                  setShowUnassignedOnly(!showUnassignedOnly);
                  if (!showUnassignedOnly) {
                    setStatusFilter('brouillon');
                    setShowRelanceOnly(false);
                  } else {
                    setStatusFilter('all');
                  }
                }}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                  showUnassignedOnly 
                    ? "bg-red-100 text-red-700 font-medium animate-pulse" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                )}
                data-testid="toggle-unassigned-btn"
              >
                <UserPlus className="w-4 h-4" />
                À affecter
                {!showUnassignedOnly && filteredDevis.filter(d => d.status === 'brouillon' && (!d.assigned_to || d.assigned_to === '')).length > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                    {filteredDevis.filter(d => d.status === 'brouillon' && (!d.assigned_to || d.assigned_to === '')).length}
                  </span>
                )}
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {filteredDevis.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                {searchTerm || statusFilter !== 'all'
                  ? 'Aucun devis trouvé'
                  : 'Aucun devis pour le moment'}
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-slate-100">
                  {groupBy === 'none' ? (
                    sortedDevis.map((d) => (
                      <Link 
                        key={d.id} 
                        to={`/admin/devis/${d.id}`}
                        className="block p-4 hover:bg-slate-50 transition"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs font-medium text-slate-800">{d.reference}</span>
                              <Badge className={cn('text-xs', getStatusColor(d.status))}>
                                {getStatusLabel(d.status)}
                              </Badge>
                            </div>
                            <p className="font-medium text-slate-900 truncate">{d.client_name}</p>
                            {d.mini_pelle_name && (
                              <p className="text-sm text-slate-500 truncate">{d.mini_pelle_name}</p>
                            )}
                            <div className="flex items-center justify-between mt-2">
                              <span className="font-mono text-sm font-semibold text-slate-800">
                                {formatPrice(d.total || 0)}
                              </span>
                              <span className="text-xs text-slate-400">
                                {formatDate(d.created_at)}
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
                        </div>
                      </Link>
                    ))
                  ) : (
                    Object.entries(groupedDevis).map(([groupKey, group]) => {
                      if (!group || !Array.isArray(group.items)) return null;
                      return (
                      <div key={groupKey}>
                        <div className="px-4 py-2 bg-slate-100 text-sm font-semibold text-slate-700 flex items-center justify-between">
                          <span>{group.label} ({group.items.length})</span>
                          <span className="font-mono text-xs">{formatPrice(group.total)}</span>
                        </div>
                        {group.items.map((d) => (
                          <Link 
                            key={d.id} 
                            to={`/admin/devis/${d.id}`}
                            className="block p-4 hover:bg-slate-50 transition"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-mono text-xs font-medium text-slate-800">{d.reference}</span>
                                  <Badge className={cn('text-xs', getStatusColor(d.status))}>
                                    {getStatusLabel(d.status)}
                                  </Badge>
                                </div>
                                <p className="font-medium text-slate-900 truncate">{d.client_name}</p>
                                <div className="flex items-center justify-between mt-2">
                                  <span className="font-mono text-sm font-semibold text-slate-800">
                                    {formatPrice(d.total || 0)}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    {formatDate(d.created_at)}
                                  </span>
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    );
                    })
                  )}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                <Table className="w-full table-fixed text-xs [&_th]:px-2 [&_th]:py-2 [&_td]:px-2 [&_td]:py-2">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      {/* Selection checkbox */}
                      <TableHead className="w-10">
                        <Checkbox
                          checked={isAllSelected()}
                          onCheckedChange={handleSelectAll}
                          aria-label="Sélectionner tout"
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      {visibleColumns.reference && (
                      <TableHead 
                        className="w-[76px] text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('reference')}
                      >
                        <div className="flex items-center">
                          Réf.
                          <SortIcon columnKey="reference" />
                        </div>
                      </TableHead>
                      )}
                      {visibleColumns.client && (
                      <TableHead 
                        className="w-[180px] text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('client_name')}
                      >
                        <div className="flex items-center">
                          Client
                          <SortIcon columnKey="client_name" />
                        </div>
                      </TableHead>
                      )}
                      {visibleColumns.machine && (
                      <TableHead 
                        className="w-[140px] text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:bg-slate-100 hidden md:table-cell"
                        onClick={() => handleSort('mini_pelle_name')}
                      >
                        <div className="flex items-center">
                          Machine
                          <SortIcon columnKey="mini_pelle_name" />
                        </div>
                      </TableHead>
                      )}
                      {visibleColumns.status && (
                      <TableHead 
                        className="w-[92px] text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          Statut
                          <SortIcon columnKey="status" />
                        </div>
                      </TableHead>
                      )}
                      {visibleColumns.financement && (
                      <TableHead className="w-[150px] text-xs font-bold uppercase tracking-wider text-slate-500">
                        <div className="flex items-center">
                          Financement
                        </div>
                      </TableHead>
                      )}
                      <TableHead 
                        className="w-[110px] text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:bg-slate-100 hidden lg:table-cell"
                        onClick={() => handleSort('cabinet_snc_name')}
                      >
                        <div className="flex items-center">
                          SNC
                          <SortIcon columnKey="cabinet_snc_name" />
                        </div>
                      </TableHead>
                      {visibleColumns.banque && (
                      <TableHead 
                        className="w-[120px] text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell"
                      >
                        <div className="flex items-center">
                          Banque
                        </div>
                      </TableHead>
                      )}
                      {visibleColumns.total && (
                      <TableHead 
                        className="w-[110px] text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('total')}
                      >
                        <div className="flex items-center">
                          Total
                          <SortIcon columnKey="total" />
                        </div>
                      </TableHead>
                      )}
                      {isAdmin() && visibleColumns.vendeur && (
                        <TableHead 
                          className="w-[110px] text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:bg-slate-100 hidden xl:table-cell"
                          onClick={() => handleSort('assigned_to_name')}
                        >
                          <div className="flex items-center">
                            Vendeur
                            <SortIcon columnKey="assigned_to_name" />
                          </div>
                        </TableHead>
                      )}
                      {visibleColumns.date && (
                      <TableHead 
                        className="w-[110px] text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:bg-slate-100 hidden sm:table-cell"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center">
                          Date
                          <SortIcon columnKey="created_at" />
                        </div>
                      </TableHead>
                      )}
                      {visibleColumns.actions && (
                      <TableHead className="w-[84px] text-xs font-bold uppercase tracking-wider text-slate-500 text-right">
                        Actions
                      </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupBy !== 'none' && groupedDevis ? (
                      // Grouped view
                      Object.entries(groupedDevis).map(([groupKey, group]) => {
                        // Protection contre les groupes malformés
                        if (!group || !Array.isArray(group.items)) return null;
                        
                        return (
                        <React.Fragment key={groupKey}>
                          {/* Group header row */}
                          <TableRow 
                            className="bg-slate-50 hover:bg-slate-100 cursor-pointer"
                            onClick={() => toggleGroup(groupKey)}
                          >
                            <TableCell colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="py-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {collapsedGroups[groupKey] ? (
                                    <ChevronRight className="w-4 h-4 text-slate-500" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-slate-500" />
                                  )}
                                  <span className="font-semibold text-slate-700">{group.label}</span>
                                  <Badge variant="outline" className="ml-2">{group.items.length}</Badge>
                                </div>
                                <span className="text-sm font-mono text-slate-600">{formatPrice(group.total)}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                          {/* Group items */}
                          {!collapsedGroups[groupKey] && group.items.map((d) => {
                            const isSelected = selectedDevis.includes(d.id);
                            return (
                            <TableRow 
                              key={d.id} 
                              data-testid={`devis-row-${d.id}`}
                              className={cn(
                                "cursor-pointer hover:bg-slate-50 transition-colors",
                                d.status === 'brouillon' && (!d.assigned_to || d.assigned_to === '') && "bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500",
                                isSelected && "bg-orange-50"
                              )}
                            >
                              {/* Checkbox cell — visible sur tous les devis */}
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => handleSelectDevis(d.id, checked)}
                                  aria-label={`Sélectionner ${d.reference}`}
                                  data-testid={`select-devis-${d.id}`}
                                />
                              </TableCell>
                              {visibleColumns.reference && (
                              <TableCell onClick={() => navigate(`/admin/devis/${d.id}`)}>
                                <div className="flex items-center gap-2 pl-4">
                                  <FileText className="w-4 h-4 text-slate-400 hidden sm:block" />
                                  <span className="font-mono text-xs sm:text-sm font-medium text-slate-800">
                                    {d.reference.split('-').pop()}
                                  </span>
                                </div>
                              </TableCell>
                              )}
                              {visibleColumns.client && (
                              <TableCell className="font-medium text-slate-800 max-w-[180px] truncate" onClick={() => navigate(`/admin/devis/${d.id}`)}>
                                {d.client_name}
                              </TableCell>
                              )}
                              {visibleColumns.machine && (
                              <TableCell className="text-slate-600 hidden md:table-cell max-w-[140px] truncate" onClick={() => navigate(`/admin/devis/${d.id}`)}>
                                {d.mini_pelle_name}
                              </TableCell>
                              )}
                              {visibleColumns.status && (
                              <TableCell onClick={() => navigate(`/admin/devis/${d.id}`)}>
                                <Badge className={cn("text-xs", getStatusColor(d.status))}>
                                  {getStatusLabel(d.status)}
                                </Badge>
                              </TableCell>
                              )}
                              {visibleColumns.financement && (
                              <TableCell>
                                {d.financing_request_id ? (
                                  <Link 
                                    to={`/admin/financement?request=${d.financing_request_id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center gap-2 hover:bg-slate-100 rounded p-1 -m-1 transition-colors"
                                    title="Voir le dossier de financement"
                                  >
                                    <div className="w-24 bg-slate-200 rounded-full h-3">
                                      <div 
                                        className={cn(
                                          "h-3 rounded-full transition-all",
                                          d.financing_progress >= 100 ? "bg-green-500" :
                                          d.financing_progress >= 50 ? "bg-blue-500" :
                                          "bg-orange-500"
                                        )}
                                        style={{ width: `${d.financing_progress || 0}%` }}
                                      />
                                    </div>
                                    <span className="text-xs font-medium text-slate-600 whitespace-nowrap">
                                      {d.financing_docs_completed || 0}/{d.financing_docs_total || 0}
                                    </span>
                                    <ExternalLink className="w-3 h-3 text-slate-400" />
                                  </Link>
                                ) : (
                                  <span className="text-slate-400 text-sm">-</span>
                                )}
                              </TableCell>
                              )}
                              <TableCell className="hidden lg:table-cell" onClick={() => navigate(`/admin/devis/${d.id}`)}>
                                {d.cabinet_snc_name ? (
                                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">
                                    <Building2 className="w-3 h-3 mr-1" />
                                    <span className="truncate max-w-[80px]">{d.cabinet_snc_name}</span>
                                  </Badge>
                                ) : (
                                  <span className="text-slate-400 text-sm">-</span>
                                )}
                              </TableCell>
                              {visibleColumns.banque && (
                              <TableCell className="hidden lg:table-cell">
                                {d.banques && d.banques.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {d.banques.slice(0, 2).map((banque, idx) => (
                                      <Badge key={idx} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                                        <Banknote className="w-3 h-3 mr-1" />
                                        <span className="truncate max-w-[60px]">{banque.name}</span>
                                      </Badge>
                                    ))}
                                    {d.banques.length > 2 && (
                                      <Badge variant="outline" className="bg-slate-50 text-slate-600 text-xs">
                                        +{d.banques.length - 2}
                                      </Badge>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-sm">-</span>
                                )}
                              </TableCell>
                              )}
                              {visibleColumns.total && (
                              <TableCell className="font-mono text-xs sm:text-sm font-medium text-slate-800">
                                {formatPrice(d.total)}
                              </TableCell>
                              )}
                              {isAdmin() && visibleColumns.vendeur && (
                                <TableCell className="text-slate-600 hidden xl:table-cell truncate">
                                  {d.assigned_to_name}
                                </TableCell>
                              )}
                              {visibleColumns.date && (
                              <TableCell className="text-xs text-slate-500 hidden sm:table-cell whitespace-nowrap">
                                {formatDate(d.created_at)}
                              </TableCell>
                              )}
                              {visibleColumns.actions && (
                              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-end gap-1">
                                  <Link to={`/admin/devis/${d.id}`}>
                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </Link>
                                </div>
                              </TableCell>
                              )}
                            </TableRow>
                          );
                          })}
                        </React.Fragment>
                      );
                      })
                    ) : (
                      // Normal view
                      sortedDevis.map((d) => {
                        const isSelected = selectedDevis.includes(d.id);
                        return (
                      <TableRow 
                        key={d.id} 
                        data-testid={`devis-row-${d.id}`}
                        className={cn(
                          "cursor-pointer hover:bg-slate-50 transition-colors",
                          d.status === 'brouillon' && (!d.assigned_to || d.assigned_to === '') && "bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500",
                          isSelected && "bg-orange-50"
                        )}
                      >
                        {/* Checkbox cell */}
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => handleSelectDevis(d.id, checked)}
                            aria-label={`Sélectionner ${d.reference}`}
                            data-testid={`select-devis-${d.id}`}
                          />
                        </TableCell>
                        {visibleColumns.reference && (
                        <TableCell onClick={() => navigate(`/admin/devis/${d.id}`)}>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400 hidden sm:block" />
                            <span className="font-mono text-xs sm:text-sm font-medium text-slate-800">
                              {d.reference.split('-').pop()}
                            </span>
                          </div>
                        </TableCell>
                        )}
                        {visibleColumns.client && (
                        <TableCell className="font-medium text-slate-800 max-w-[180px] truncate" onClick={() => navigate(`/admin/devis/${d.id}`)}>
                          {d.client_name}
                        </TableCell>
                        )}
                        {visibleColumns.machine && (
                        <TableCell className="text-slate-600 hidden md:table-cell max-w-[140px] truncate" onClick={() => navigate(`/admin/devis/${d.id}`)}>
                          {d.mini_pelle_name}
                        </TableCell>
                        )}
                        {visibleColumns.status && (
                        <TableCell onClick={() => navigate(`/admin/devis/${d.id}`)}>
                          <Badge className={cn("text-xs", getStatusColor(d.status))}>
                            {getStatusLabel(d.status)}
                          </Badge>
                        </TableCell>
                        )}
                        {visibleColumns.financement && (
                        <TableCell>
                          {d.financing_request_id ? (
                            <Link 
                              to={`/admin/financement?request=${d.financing_request_id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2 hover:bg-slate-100 rounded p-1 -m-1 transition-colors"
                              title="Voir le dossier de financement"
                            >
                              <div className="w-24 bg-slate-200 rounded-full h-3">
                                <div 
                                  className={cn(
                                    "h-3 rounded-full transition-all",
                                    d.financing_progress >= 100 ? "bg-green-500" :
                                    d.financing_progress >= 50 ? "bg-blue-500" :
                                    "bg-orange-500"
                                  )}
                                  style={{ width: `${d.financing_progress || 0}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium text-slate-600 whitespace-nowrap">
                                {d.financing_docs_completed || 0}/{d.financing_docs_total || 0}
                              </span>
                              <ExternalLink className="w-3 h-3 text-slate-400" />
                            </Link>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        )}
                        <TableCell className="hidden lg:table-cell" onClick={() => navigate(`/admin/devis/${d.id}`)}>
                          {d.cabinet_snc_name ? (
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">
                              <Building2 className="w-3 h-3 mr-1" />
                              <span className="truncate max-w-[80px]">{d.cabinet_snc_name}</span>
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        {visibleColumns.banque && (
                        <TableCell className="hidden lg:table-cell" onClick={() => navigate(`/admin/devis/${d.id}`)}>
                          {d.banques && d.banques.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {d.banques.slice(0, 2).map((banque, idx) => (
                                <Badge key={idx} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                                  <Banknote className="w-3 h-3 mr-1" />
                                  <span className="truncate max-w-[60px]">{banque.name}</span>
                                </Badge>
                              ))}
                              {d.banques.length > 2 && (
                                <Badge variant="outline" className="bg-slate-50 text-slate-600 text-xs">
                                  +{d.banques.length - 2}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        )}
                        {visibleColumns.total && (
                        <TableCell className="font-mono text-xs sm:text-sm font-medium text-slate-800" onClick={() => navigate(`/admin/devis/${d.id}`)}>
                          {formatPrice(d.total)}
                        </TableCell>
                        )}
                        {isAdmin() && visibleColumns.vendeur && (
                          <TableCell className="text-slate-600 hidden xl:table-cell truncate" onClick={() => navigate(`/admin/devis/${d.id}`)}>
                            {d.assigned_to_name}
                          </TableCell>
                        )}
                        {visibleColumns.date && (
                        <TableCell className="text-xs text-slate-500 hidden sm:table-cell whitespace-nowrap" onClick={() => navigate(`/admin/devis/${d.id}`)}>
                          {formatDate(d.created_at)}
                        </TableCell>
                        )}
                        {visibleColumns.actions && (
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Link to={`/admin/devis/${d.id}`}>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`view-devis-${d.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`actions-devis-${d.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleDuplicate(d.id)}>
                                  <Copy className="w-4 h-4 mr-2" />
                                  Dupliquer
                                </DropdownMenuItem>
                                {d.status !== 'archive' && (
                                  <DropdownMenuItem onClick={() => handleArchive(d.id)}>
                                    <Archive className="w-4 h-4 mr-2" />
                                    Archiver
                                  </DropdownMenuItem>
                                )}
                                <Link to={`/admin/devis/${d.id}/modifier`}>
                                  <DropdownMenuItem>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Modifier
                                  </DropdownMenuItem>
                                </Link>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                        )}
                      </TableRow>
                    );
                    })
                    )}
                  </TableBody>
                </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5" style={{ gridTemplateColumns: `repeat(${Math.min(kanbanStatuses.length, 6)}, minmax(250px, 1fr))` }}>
          {kanbanStatuses.map((status) => {
            const statusDevis = getDevisByStatus(status.id);
            const totalCA = statusDevis.reduce((sum, d) => sum + d.total, 0);
            const isDragOver = dragOverColumn === status.id;
            
            return (
              <div 
                key={status.id} 
                className="flex flex-col" 
                data-testid={`kanban-column-${status.id}`}
                onDragOver={(e) => handleDragOver(e, status.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, status.id)}
              >
                <div className={cn(
                  "px-3 py-2 rounded-t-lg flex items-center justify-between",
                  status.color
                )}>
                  <div className="flex items-center gap-2">
                    <status.icon className="w-4 h-4" />
                    <span className="font-semibold text-sm">{status.label}</span>
                  </div>
                  <span className="text-xs font-mono bg-white/50 px-2 py-0.5 rounded">
                    {statusDevis.length}
                  </span>
                </div>
                
                <div className={cn(
                  "bg-slate-50 rounded-b-lg p-2 min-h-[200px] flex flex-col gap-2 transition-colors",
                  isDragOver && "bg-blue-50 ring-2 ring-blue-300 ring-inset"
                )}>
                  {/* Total CA for column */}
                  {statusDevis.length > 0 && (
                    <div className="text-xs text-slate-500 text-center py-1 border-b border-slate-200 mb-1">
                      CA: {formatPrice(totalCA)}
                    </div>
                  )}
                  
                  {statusDevis.length === 0 ? (
                    <div className={cn(
                      "flex-1 flex items-center justify-center text-sm",
                      isDragOver ? "text-blue-500" : "text-slate-400"
                    )}>
                      {isDragOver ? "Déposer ici" : "Aucun devis"}
                    </div>
                  ) : (
                    statusDevis.map((d) => (
                      <Card 
                        key={d.id} 
                        className={cn(
                          "border-slate-200 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing",
                          draggedDevis?.id === d.id && "opacity-50 ring-2 ring-yellow-400"
                        )}
                        data-testid={`kanban-card-${d.id}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, d)}
                        onDragEnd={handleDragEnd}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <GripVertical className="w-3 h-3 text-slate-300 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <Link to={`/admin/devis/${d.id}`}>
                                  <p className="font-mono text-xs text-slate-500 hover:text-yellow-600">
                                    {d.reference}
                                  </p>
                                  <p className="font-medium text-slate-800 truncate text-sm mt-1">
                                    {d.client_name}
                                  </p>
                                </Link>
                                <p className="text-xs text-slate-500 truncate">
                                  {d.mini_pelle_name}
                                </p>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6">
                                  <MoreHorizontal className="w-3 h-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {STATUSES.filter(s => s.id !== d.status).map((s) => (
                                  <DropdownMenuItem
                                    key={s.id}
                                    onClick={() => handleStatusChange(d.id, s.id)}
                                  >
                                    <s.icon className="w-3 h-3 mr-2" />
                                    {s.label}
                                  </DropdownMenuItem>
                                ))}
                                <DropdownMenuItem onClick={() => handleDuplicate(d.id)}>
                                  <Copy className="w-3 h-3 mr-2" />
                                  Dupliquer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                            <span className="text-xs text-slate-400">
                              {formatDate(d.created_at)}
                            </span>
                            <span className="font-mono text-sm font-semibold text-yellow-600">
                              {formatPrice(d.total)}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DevisListPage;
